import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { ArrayItemsEditor } from './ArrayItemsEditor';
import type { PropertyData } from '@/domain/entities/property-object';
import {
  BillFrequency,
  BillStatus,
  BillUtilityType,
  type BillFrequency as BillFrequencyValue,
  type BillStatus as BillStatusValue,
  type BillUtilityType as BillUtilityTypeValue,
  type FrequencyInput,
  type IUtility,
} from '@/domain/entities/utility';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { RootState } from '@/store/root-state';

import {
  fetchUtilitiesByPropertyId,
  createUtility,
  updateUtility,
  deleteUtility,
} from '@/features/utilities/utilitiesThunks';

import {
  selectUtilitiesByPropertyId,
  selectUtilitiesListingByPropertyId,
  selectUtilitiesFetchedByPropertyId,
  setUtilitiesForProperty,
  selectUtilitiesDirtyByPropertyId,
  markUtilityDirty,
  markUtilityClean,
  selectUtilitiesActiveSort,
  setUtilitiesActiveSort,
} from '@/features/utilities/utilitiesSlice';

import { inputValueToUnixSeconds, sortItems, unixSecondsToInputValue } from '@/lib';
import { PerItemSaveActions } from './shared/PerItemSaveActions';
import {
  buildPropertyEditorPersistKey,
  getDirtyBorderClass,
  isTempId,
  reconcileKeepOrder,
  removeAtIndex,
} from './shared/helpers';
import { useSortedItemsOnce } from './shared/useSortedItemsOnce';
import { usePropertyEntityTabController } from './shared/usePropertyEntityTabController';
import { TabLoadBanner } from './shared/TabLoadBanner';
import { TypeNames } from '@/domain/shared/storable';
import { usePermissions } from '@/hooks/usePermissions';
import { Permissions } from '@/lib/permissions';

type BuilderMode = 'create' | 'edit';

function nowSec() {
  return Math.floor(Date.now() / 1000);
}

const BILL_FREQUENCIES = Object.values(BillFrequency) as BillFrequencyValue[];
const BILL_STATUSES = Object.values(BillStatus) as BillStatusValue[];
const BILL_UTILITY_TYPES = Object.values(BillUtilityType) as BillUtilityTypeValue[];

function makeDefaultFrequency(): FrequencyInput {
  return { frequency: BillFrequency.Monthly };
}

function makeNewUtility(propertyID: string): IUtility {
  const t = nowSec();

  return {
    id: `tmp-utility-${t}`,
    typeName: TypeNames.Bill,
    createdAt: t,
    updatedAt: t,
    title: 'New Utility',
    description: null,
    thumbnail: null,
    thumbnailUrl: null,
    photos: null,
    propertyID,
    utilityType: BillUtilityType.Other,
    frequency: makeDefaultFrequency(),
    account: null,
    payeeID: null,
    dueDate: null,
    status: BillStatus.Unpaid,
    paymentMethod: null,
    lastPayment: null,
  } as IUtility;
}

const patchItem = (item: IUtility, patch: Partial<IUtility>): IUtility => ({
  ...item,
  ...patch,
  updatedAt: nowSec(),
});

function UtilityEditorCard(props: {
  item: IUtility;
  mode: BuilderMode;
  resolvedPropertyID: string;
  setItem: (next: IUtility) => void;
  markDirty: (id: string) => void;
}) {
  const { item, mode, resolvedPropertyID, setItem, markDirty } = props;

  const set = (patch: Partial<IUtility>) => {
    const next = patchItem(item, patch);
    setItem(next);

    if (mode !== 'edit' || !next.id || !resolvedPropertyID) return;
    markDirty(next.id);
  };

  const freq: FrequencyInput = item.frequency ?? makeDefaultFrequency();

  const setFrequency = (patch: Partial<FrequencyInput>) => {
    set({
      frequency: {
        ...freq,
        ...patch,
      },
    });
  };

  return (
    <div className="space-y-8">
      {/* BASICS */}
      <div className="space-y-3">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Title</Label>
            <Input
              placeholder="e.g., Electricity"
              value={item.title ?? ''}
              onChange={(e) => set({ title: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label>Utility Type</Label>
            <Select
              value={(item.utilityType ?? BillUtilityType.Other) as BillUtilityTypeValue}
              onValueChange={(utilityType) => set({ utilityType: utilityType as BillUtilityTypeValue })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select utility type" />
              </SelectTrigger>
              <SelectContent>
                {BILL_UTILITY_TYPES.map((v) => (
                  <SelectItem key={v} value={v}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label>Description</Label>
            <Input
              placeholder="Optional notes (e.g., provider, plan, meter info)"
              value={item.description ?? ''}
              onChange={(e) => set({ description: e.target.value || null })}
            />
          </div>
        </div>
      </div>

      {/* BILLING */}
      <div className="space-y-3">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Frequency</Label>
            <Select
              value={freq.frequency}
              onValueChange={(frequency) => setFrequency({ frequency: frequency as BillFrequencyValue })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select frequency" />
              </SelectTrigger>
              <SelectContent>
                {BILL_FREQUENCIES.map((v) => (
                  <SelectItem key={v} value={v}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Status</Label>
            <Select
              value={(item.status ?? BillStatus.Unpaid) as BillStatusValue}
              onValueChange={(status) => set({ status: status as BillStatusValue })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {BILL_STATUSES.map((v) => (
                  <SelectItem key={v} value={v}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Due Date</Label>
            <Input
              type="date"
              value={item.dueDate ? unixSecondsToInputValue(item.dueDate, 'date') : ''}
              onChange={(e) => set({ dueDate: inputValueToUnixSeconds(e.target.value, 'date') })}
            />
            <p className="text-muted-foreground text-xs">Optional — used for reminders & scheduling.</p>
          </div>

          <div className="space-y-2">
            <Label>Start Date</Label>
            <Input
              type="date"
              value={freq.startDate ? unixSecondsToInputValue(freq.startDate, 'date') : ''}
              onChange={(e) => setFrequency({ startDate: inputValueToUnixSeconds(e.target.value, 'date') })}
            />
            <p className="text-muted-foreground text-xs">Optional — when this billing cycle began.</p>
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label>Month (optional)</Label>
            <Input
              placeholder="e.g., January / 2026-01 (if needed later)"
              value={freq.month ?? ''}
              onChange={(e) => setFrequency({ month: e.target.value || null })}
            />
          </div>
        </div>
      </div>

      {/* ACCOUNT */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-muted-foreground text-xs">Optional</div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Account</Label>
            <Input
              placeholder="Not set"
              value={item.account ?? ''}
              onChange={(e) => set({ account: e.target.value || null })}
            />
          </div>

          <div className="space-y-2">
            <Label>Payee ID</Label>
            <Input
              placeholder="Not set"
              value={item.payeeID ?? ''}
              onChange={(e) => set({ payeeID: e.target.value || null })}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export function UtilitiesTab(props: {
  data: PropertyData;
  setData: (next: PropertyData) => void;
  mode: BuilderMode;
  propertyId?: string;
}) {
  const { data, setData, mode, propertyId } = props;
  const dispatch = useAppDispatch();
  const { hasPermission, isSuperAdmin } = usePermissions();
  const canCreate = mode === 'create' || isSuperAdmin || hasPermission(Permissions.UtilitiesCreate);
  const canUpdate = mode === 'create' || isSuperAdmin || hasPermission(Permissions.UtilitiesUpdate);
  const canDelete = mode === 'create' || isSuperAdmin || hasPermission(Permissions.UtilitiesDelete);

  const resolvedPropertyID = data.property?.id ?? propertyId ?? '';

  const activeSort = useAppSelector(selectUtilitiesActiveSort);

  const dirtyById = useAppSelector((s) => selectUtilitiesDirtyByPropertyId(s, resolvedPropertyID));

  const localItems = data.utilities ?? [];
  const storeItems = useAppSelector((s) => selectUtilitiesByPropertyId(s, resolvedPropertyID));
  const effectiveItems = mode === 'edit' ? storeItems : localItems;

  const isLoading = useAppSelector((s) => selectUtilitiesListingByPropertyId(s, resolvedPropertyID));
  const hasFetched = useAppSelector((s) => selectUtilitiesFetchedByPropertyId(s, resolvedPropertyID));
  const error = useAppSelector((s: RootState) =>
    resolvedPropertyID ? (s.utilities?.errorByPropertyID?.[resolvedPropertyID] ?? null) : null,
  );

  const scopeKey = `${mode}:${resolvedPropertyID || 'new'}`;
  const { sortedItems, setSortedItems, resort } = useSortedItemsOnce<IUtility>({
    scopeKey,
    items: effectiveItems,
    enabled: mode === 'edit' ? hasFetched : false,
    activeSort,
    sort: (arr, spec) => sortItems(arr, spec) as IUtility[],
    reconcile: (prev, next) => reconcileKeepOrder(prev, next, { insertNew: 'top' }) as IUtility[],
  });

  const markDirty = (id: string) => {
    if (!resolvedPropertyID) return;
    dispatch(markUtilityDirty({ propertyID: resolvedPropertyID, utilityID: id }));
  };

  const markClean = (id: string) => {
    if (!resolvedPropertyID) return;
    dispatch(markUtilityClean({ propertyID: resolvedPropertyID, id }));
  };

  const ctrl = usePropertyEntityTabController<IUtility>({
    mode,
    resolvedPropertyID,
    localItems,
    storeItems,
    writeLocal: (next) => setData({ ...data, utilities: next }),
    writeStore: (next) => dispatch(setUtilitiesForProperty({ propertyID: resolvedPropertyID, items: next })),
    hasFetched,
    fetchRemote: () => dispatch(fetchUtilitiesByPropertyId({ propertyID: resolvedPropertyID })),
    markDirty,
    markClean,
    createRemote: (u, tempId) => dispatch(createUtility({ utility: u, tempId })).unwrap(),
    updateRemote: (u) => dispatch(updateUtility({ id: u.id!, propertyID: resolvedPropertyID, patch: u })).unwrap(),
    deleteRemote: (id) => dispatch(deleteUtility({ id, propertyID: resolvedPropertyID })).unwrap(),
    entityName: 'utility',
    isTempId,
    normalizeBeforeSave: (u) => ({
      ...u,
      propertyID: u.propertyID ?? resolvedPropertyID,
      updatedAt: nowSec(),
    }),
  });

  return (
    <div className="space-y-3">
      <TabLoadBanner mode={mode} isLoading={isLoading} error={error} loadingLabel="Loading utilities…" />

      <ArrayItemsEditor<IUtility>
        title="Utilities"
        persistKey={buildPropertyEditorPersistKey({ propertyID: resolvedPropertyID, tab: 'utilities' })}
        items={sortedItems}
        onChange={ctrl.writeAll}
        createItem={() => makeNewUtility(resolvedPropertyID || 'unknown-property')}
        canAdd={canCreate}
        canEdit={(item) => (isTempId(item.id) ? canCreate : canUpdate)}
        canRemove={(item) => (isTempId(item.id) ? canCreate : canDelete)}
        getItemTitle={(x, idx) => x.title || `Utility #${idx + 1}`}
        sortItems={(_items, sortSpec) => {
          setSortedItems(sortItems(_items, sortSpec) as IUtility[]);
          dispatch(setUtilitiesActiveSort(sortSpec));
        }}
        resortItems={resort}
        activeSort={activeSort}
        getItemClassName={(u) =>
          getDirtyBorderClass({
            mode,
            id: u.id,
            dirtyById,
            isTempId,
          })
        }
        rightHeader={({ item }) => {
          if (mode !== 'edit' || !item.id) return null;

          const id = item.id;
          const saving = !!ctrl.savingById[id];
          const canSave = (dirtyById[id] ?? false) || isTempId(id);
          const err = ctrl.saveErrorById[id];
          const mayCreateOrUpdate = isTempId(id) ? canCreate : canUpdate;
          const mayDelete = isTempId(id) ? canCreate : canDelete;

          if (!mayCreateOrUpdate && !mayDelete && !err) return null;

          return (
            <PerItemSaveActions
              error={err}
              saving={saving}
              canSave={canSave}
              saveLabel={isTempId(id) ? 'Save' : 'Update'}
              onDelete={mayDelete ? () => void ctrl.deleteItem(item) : undefined}
              onSave={mayCreateOrUpdate ? () => void ctrl.saveItem(item) : undefined}
            />
          );
        }}
        onRemove={(item, idx) => {
          if (mode === 'edit') {
            void ctrl.deleteItem(item);
            return;
          }
          setData({ ...data, utilities: removeAtIndex(data.utilities, idx) });
        }}
        onRefresh={() => {
          if (mode !== 'edit') return;
          if (!resolvedPropertyID) return;
          void dispatch(fetchUtilitiesByPropertyId({ propertyID: resolvedPropertyID }));
        }}
        isRefreshing={isLoading}
        renderItem={({ item, setItem }) => (
          <UtilityEditorCard
            item={item}
            mode={mode}
            resolvedPropertyID={resolvedPropertyID}
            setItem={setItem}
            markDirty={markDirty}
          />
        )}
      />
    </div>
  );
}
